import React, {useMemo, useState} from "react";
import {Alert, Button, Card, Grid, Input, InputNumber, List, Modal, Select, Space, Switch, Table, Tag, Tooltip, Typography, message} from "antd";
import {ExclamationCircleFilled, ReloadOutlined, SettingOutlined} from "@ant-design/icons";
import {useFeatureFlags, useUpdateFeatureFlag} from "./feature-flags-query";

const {Text} = Typography;

/**
 * Turning capabilities on and off without a deploy (roadmap 0.6).
 *
 * <p>The switch is the point of the screen, so it is one click and it applies immediately. Narrowing
 * a rollout is behind a second dialog, because that is a considered change rather than the thing
 * somebody reaches for when a feature is misbehaving.
 */
const FeatureFlags = () => {
    const screens = Grid.useBreakpoint();
    const isDesktop = !!screens.md;

    const {data, isLoading, isFetching, refetch} = useFeatureFlags();
    const {mutate: update, isPending} = useUpdateFeatureFlag();

    const [editing, setEditing] = useState(null);
    const [draft, setDraft] = useState({});

    const rows = useMemo(() => data?.data || data || [], [data]);

    const toggle = (row, next) => {
        const apply = () => update(
            {key: row.flagKey, patch: {enabled: next}},
            {
                onSuccess: () => message.success(next ? "قابلیت روشن شد" : "قابلیت خاموش شد"),
                onError: () => message.error("تغییر وضعیت ناموفق بود"),
            },
        );
        if (!next) {
            // Turning something off is the safe direction — no confirmation, it needs to be fast.
            apply();
            return;
        }
        Modal.confirm({
            title: "روشن کردن قابلیت",
            icon: <ExclamationCircleFilled style={{color: "#faad14"}}/>,
            content: (
                <div>
                    <p>«{row.flagKey}» برای کاربران فعال می‌شود.</p>
                    {row.rolloutPercentage < 100 && (
                        <Alert type="info" showIcon style={{marginTop: 8}}
                               message={`فقط برای ${row.rolloutPercentage}٪ از مخاطبان فعال خواهد شد.`}/>
                    )}
                </div>
            ),
            okText: "بله، روشن کن",
            cancelText: "انصراف",
            onOk: apply,
        });
    };

    const openScope = (row) => {
        setEditing(row);
        setDraft({
            rolloutPercentage: row.rolloutPercentage ?? 100,
            channels: row.channels ? row.channels.split(",").map((s) => s.trim()).filter(Boolean) : [],
            environments: row.environments || "",
            operatorIds: row.operatorIds || "",
        });
    };

    const saveScope = () => {
        update(
            {
                key: editing.flagKey,
                patch: {
                    rolloutPercentage: draft.rolloutPercentage,
                    // Empty string, not null: null means "unchanged", empty means "no restriction".
                    channels: (draft.channels || []).join(","),
                    environments: draft.environments || "",
                    operatorIds: draft.operatorIds || "",
                },
            },
            {
                onSuccess: () => {
                    message.success("محدوده به‌روزرسانی شد");
                    setEditing(null);
                },
                onError: () => message.error("ذخیره ناموفق بود"),
            },
        );
    };

    const scopeSummary = (row) => {
        const bits = [];
        if (row.rolloutPercentage != null && row.rolloutPercentage < 100) bits.push(`${row.rolloutPercentage}٪`);
        if (row.channels) bits.push(row.channels);
        if (row.environments) bits.push(row.environments);
        if (row.operatorIds) bits.push("اپراتورهای منتخب");
        return bits.length ? bits.join(" · ") : "همه";
    };

    const columns = useMemo(() => [
        {
            title: "قابلیت",
            dataIndex: "flagKey",
            render: (v, row) => (
                <Space direction="vertical" size={0}>
                    <Text code>{v}</Text>
                    {row.description && <Text type="secondary" style={{fontSize: 12}}>{row.description}</Text>}
                </Space>
            ),
        },
        {
            title: "وضعیت",
            dataIndex: "enabled",
            width: 110,
            render: (v, row) => (
                <Switch checked={!!v} loading={isPending} onChange={(next) => toggle(row, next)}/>
            ),
        },
        {title: "محدوده", key: "scope", width: 200, render: (_, row) => <Text>{scopeSummary(row)}</Text>},
        {
            title: "آخرین تغییر",
            key: "updated",
            width: 190,
            render: (_, row) => (
                <Space direction="vertical" size={0}>
                    <Text style={{fontSize: 12}}>
                        {row.updatedAt ? new Date(row.updatedAt).toLocaleString("fa-IR") : "—"}
                    </Text>
                    <Text type="secondary" style={{fontSize: 12}}>{row.updatedByName || ""}</Text>
                </Space>
            ),
        },
        {
            title: "",
            key: "actions",
            width: 110,
            render: (_, row) => (
                <Tooltip title="تعیین محدوده">
                    <Button icon={<SettingOutlined/>} onClick={() => openScope(row)}>محدوده</Button>
                </Tooltip>
            ),
        },
    ], [isPending]);

    return (
        <div style={{padding: 12}}>
            <Card
                title="قابلیت‌ها"
                extra={
                    <Button icon={<ReloadOutlined/>} onClick={() => refetch()} loading={isFetching}>
                        به‌روزرسانی
                    </Button>
                }
            >
                <Alert
                    type="info"
                    showIcon
                    style={{marginBottom: 12}}
                    message="تغییرات بلافاصله اعمال می‌شوند"
                    description="خاموش کردن یک قابلیت نیازی به انتشار نسخه جدید ندارد و ظرف چند ثانیه اثر می‌گذارد. هر تغییر در «گزارش رخدادها» ثبت می‌شود."
                />

                {isDesktop ? (
                    <Table
                        rowKey="flagKey"
                        loading={isLoading}
                        dataSource={rows}
                        columns={columns}
                        pagination={false}
                        size="small"
                        scroll={{x: 900}}
                        locale={{emptyText: "قابلیتی تعریف نشده است"}}
                    />
                ) : (
                    <List
                        loading={isLoading}
                        dataSource={rows}
                        locale={{emptyText: "قابلیتی تعریف نشده است"}}
                        renderItem={(row) => (
                            <List.Item actions={[
                                <Switch key="s" checked={!!row.enabled} onChange={(n) => toggle(row, n)}/>,
                            ]}>
                                <Space direction="vertical" size={2}>
                                    <Text code>{row.flagKey}</Text>
                                    <Text type="secondary" style={{fontSize: 12}}>{row.description}</Text>
                                    <Tag>{scopeSummary(row)}</Tag>
                                </Space>
                            </List.Item>
                        )}
                    />
                )}
            </Card>

            <Modal
                open={!!editing}
                title={editing ? `محدوده «${editing.flagKey}»` : ""}
                onCancel={() => setEditing(null)}
                onOk={saveScope}
                okText="ذخیره"
                cancelText="انصراف"
                confirmLoading={isPending}
            >
                <Space direction="vertical" size={12} style={{width: "100%"}}>
                    <div>
                        <Text>درصد مخاطبان</Text>
                        <InputNumber
                            min={0} max={100} style={{width: "100%"}}
                            value={draft.rolloutPercentage}
                            onChange={(v) => setDraft((d) => ({...d, rolloutPercentage: v}))}
                        />
                        <Text type="secondary" style={{fontSize: 12}}>
                            هر کاربر همیشه همان نتیجه را می‌گیرد؛ قابلیت وسط گفت‌وگو روشن و خاموش نمی‌شود.
                        </Text>
                    </div>
                    <div>
                        <Text>کانال‌ها</Text>
                        <Select
                            mode="multiple" allowClear style={{width: "100%"}} placeholder="همه کانال‌ها"
                            value={draft.channels}
                            onChange={(v) => setDraft((d) => ({...d, channels: v}))}
                            options={[{label: "چت سایت", value: "WEB"}, {label: "تلگرام", value: "TELEGRAM"}]}
                        />
                    </div>
                    <div>
                        <Text>محیط‌ها</Text>
                        <Input
                            allowClear placeholder="خالی = همه محیط‌ها (مثلاً: local)"
                            value={draft.environments}
                            onChange={(e) => setDraft((d) => ({...d, environments: e.target.value}))}
                        />
                    </div>
                    <div>
                        <Text>شناسه اپراتورها</Text>
                        <Input
                            allowClear placeholder="خالی = همه اپراتورها (با کاما جدا کنید)"
                            value={draft.operatorIds}
                            onChange={(e) => setDraft((d) => ({...d, operatorIds: e.target.value}))}
                        />
                    </div>
                </Space>
            </Modal>
        </div>
    );
};

export default FeatureFlags;
